import React, { useState, useRef, useEffect } from "react";
import { ArrowUp, Square, X, FileText, Image as ImageIcon, File as FileIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface ChatEnterInputProps {
  placeholder?: string;
  isLoading?: boolean;
  disabled?: boolean;
  onSend: (text: string, files?: File[]) => void | Promise<void>;
  className?: string;
  
  // File handling
  selectedFile?: File | null;
  onFileSelect?: (file: File | null) => void;
  imagePreviews?: { file: File; url: string }[];
  onRemoveImage?: (index: number) => void;
  pendingFiles?: File[];
  onRemovePendingFile?: (index: number) => void;
  onAttachmentClick?: () => void;
  
  // Voice recording
  onVoiceRecording?: (audioBlob: Blob, duration: number) => Promise<void>;
  isRecording?: boolean;
  isTranscribing?: boolean;
  duration?: number;
  onStartRecording?: () => void;
  onStopRecording?: () => void;
  
  // Drag & Drop
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onPaste?: (e: React.ClipboardEvent) => void;
}

export default function ChatEnterInput({
  placeholder = "Faça perguntas...",
  isLoading = false,
  disabled = false,
  onSend,
  className = "",
  selectedFile,
  onFileSelect,
  imagePreviews = [],
  onRemoveImage,
  pendingFiles = [],
  onRemovePendingFile,
  onAttachmentClick,
  onVoiceRecording,
  isRecording = false,
  isTranscribing = false,
  duration = 0,
  onStartRecording,
  onStopRecording,
  onDragOver,
  onDragLeave,
  onDrop,
  onPaste,
}: ChatEnterInputProps) {
  const [value, setValue] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    const newHeight = Math.min(textarea.scrollHeight, 200);
    textarea.style.height = `${newHeight}px`;
  }, [value]);

  // Focus on mount
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Restore focus when loading state changes
  useEffect(() => {
    if (!isLoading) {
      textareaRef.current?.focus();
    }
  }, [isLoading]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (e.key !== "Enter") return;

    // IME (teclados asiáticos) — não enviar durante composição
    if ((e as any).isComposing || isComposing) return;

    // Modificadores — manter quebra de linha com Shift
    if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;

    e.preventDefault();
    handleSend();
  };

  const handleSend = () => {
    const text = value.trim();
    if (!text && !selectedFile && pendingFiles.length === 0) return;
    
    void onSend(text);
    setValue("");
    
    // Reset textarea height and restore focus
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.focus();
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getFileIcon = (file: File) => {
    if (file.type.includes('pdf')) return <FileText className="h-6 w-6 text-red-500" />;
    if (file.type.startsWith('image/')) return <ImageIcon className="h-6 w-6 text-blue-500" />;
    return <FileIcon className="h-6 w-6 text-primary" />;
  };

  return (
    <div className={cn("w-full", className)}>
      {/* Selected File Preview */}
      {selectedFile && (
        <div className="mb-2 px-2">
          <div className="flex items-center gap-3 p-2 border rounded-md bg-secondary/50">
            <div className="flex-shrink-0">
              {getFileIcon(selectedFile)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{selectedFile.name}</p>
              <p className="text-xs text-muted-foreground">
                {(selectedFile.size / 1024).toFixed(1)} KB
              </p>
            </div>
            <button
              onClick={() => onFileSelect?.(null)}
              className="flex-shrink-0 p-1 hover:bg-destructive/10 rounded"
              aria-label="Remover arquivo"
            >
              <X className="h-4 w-4 text-destructive" />
            </button>
          </div>
        </div>
      )}
      
      {/* Image Previews */}
      {imagePreviews.length > 0 && (
        <div className="mb-2 px-2">
          <div className="flex flex-wrap gap-2">
            {imagePreviews.map((preview, idx) => (
              <div key={idx} className="relative group">
                <img 
                  src={preview.url} 
                  alt="Preview" 
                  className="h-20 w-20 object-cover rounded-lg border border-border"
                />
                <button
                  onClick={() => onRemoveImage?.(idx)}
                  className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remover imagem"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Files Preview */}
      {pendingFiles.length > 0 && (
        <div className="mb-2 px-2">
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground font-medium">
              {pendingFiles.length} arquivo(s) prontos para envio
            </p>
            {pendingFiles.map((file, idx) => (
              <div key={idx} className="flex items-center gap-3 p-2 border rounded-md bg-secondary/30">
                <div className="flex-shrink-0">
                  {getFileIcon(file)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <button
                  onClick={() => onRemovePendingFile?.(idx)}
                  className="flex-shrink-0 p-1 hover:bg-destructive/10 rounded"
                  aria-label="Remover arquivo"
                >
                  <X className="h-4 w-4 text-destructive" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Input Container */}
      <div className="relative flex items-end gap-2 rounded-2xl border border-input bg-background px-3 py-2 shadow-sm focus-within:ring-1 focus-within:ring-ring">
        {/* Textarea */}
        <textarea
          ref={textareaRef}
          rows={1}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          onPaste={onPaste}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          placeholder={placeholder}
          disabled={isLoading || isRecording || disabled}
          className="flex-1 resize-none bg-transparent px-2 py-2 text-base placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 min-h-[24px] max-h-[200px] scrollbar-thin"
          style={{ height: 'auto' }}
        />

        {/* Recording Indicator */}
        {isRecording && (
          <div className="flex items-center gap-2 px-2">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-destructive rounded-full animate-pulse" />
              <span className="text-sm text-muted-foreground">{formatDuration(duration)}</span>
            </div>
            <button
              type="button"
              onClick={onStopRecording}
              disabled={disabled}
              className="p-2 text-destructive hover:text-destructive/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-lg hover:bg-accent"
              aria-label="Parar gravação"
            >
              <Square className="h-5 w-5 fill-current" />
            </button>
          </div>
        )}

        {/* Transcribing Indicator */}
        {isTranscribing && (
          <div className="flex items-center gap-2 px-2">
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
            <span className="text-sm text-muted-foreground">Transcrevendo...</span>
          </div>
        )}

        {/* Send Button */}
        {!isRecording && !isTranscribing && (
          <button
            type="button"
            disabled={isLoading || (!value.trim() && !selectedFile && pendingFiles.length === 0) || disabled}
            onClick={handleSend}
            className="flex-shrink-0 p-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-lg shadow-sm"
            aria-label="Enviar mensagem"
          >
            <ArrowUp className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
}
